import { useEffect, useRef } from 'react';
import {
  LuBold, LuItalic, LuUnderline, LuList, LuListOrdered, LuLink, LuImage, LuHeading2, LuHeading3, LuQuote, LuEraser,
} from 'react-icons/lu';
import { resizeImageFile } from './trainingUtils';

/**
 * Editor de texto formatado simples (títulos, listas, negrito, links,
 * imagens) sobre contentEditable — sem biblioteca externa. O HTML gerado é
 * SEMPRE sanitizado de novo no backend (trainingCourseService) antes de
 * gravar; este componente não é a barreira de segurança.
 */
export default function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const fileRef = useRef(null);

  // Só injeta o HTML inicial / externo — reescrever a cada tecla perde o cursor.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || '';
  }, [value]);

  const emit = () => onChange(ref.current.innerHTML);

  const exec = (cmd, arg) => {
    ref.current.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const handleLink = () => {
    const url = window.prompt('Endereço do link (https://...)');
    if (url && /^https?:\/\//i.test(url.trim())) exec('createLink', url.trim());
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      exec('insertImage', await resizeImageFile(file, 1000));
    } catch (err) {
      window.alert(err.message);
    }
  };

  const tools = [
    { title: 'Título', icon: <LuHeading2 />, run: () => exec('formatBlock', '<h2>') },
    { title: 'Subtítulo', icon: <LuHeading3 />, run: () => exec('formatBlock', '<h3>') },
    { title: 'Parágrafo', icon: <span style={{ fontWeight: 700 }}>¶</span>, run: () => exec('formatBlock', '<p>') },
    { title: 'Negrito', icon: <LuBold />, run: () => exec('bold') },
    { title: 'Itálico', icon: <LuItalic />, run: () => exec('italic') },
    { title: 'Sublinhado', icon: <LuUnderline />, run: () => exec('underline') },
    { title: 'Lista', icon: <LuList />, run: () => exec('insertUnorderedList') },
    { title: 'Lista numerada', icon: <LuListOrdered />, run: () => exec('insertOrderedList') },
    { title: 'Citação', icon: <LuQuote />, run: () => exec('formatBlock', '<blockquote>') },
    { title: 'Link', icon: <LuLink />, run: handleLink },
    { title: 'Imagem', icon: <LuImage />, run: () => fileRef.current.click() },
    { title: 'Limpar formatação', icon: <LuEraser />, run: () => exec('removeFormat') },
  ];

  return (
    <div className="rte">
      <div className="rte-toolbar">
        {tools.map((t) => (
          <button key={t.title} type="button" title={t.title} onMouseDown={(e) => e.preventDefault()} onClick={t.run}>
            {t.icon}
          </button>
        ))}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleImage} />
      </div>
      <div
        ref={ref}
        className="rte-content"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
      />
    </div>
  );
}
